import { useContext, useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  InputAdornment,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import { AppContext } from '../../context/AppContext';
import { validatePrice } from '../../utils/validation';

function displaySymbolForCurrency(currency) {
  if (!currency) return '$';
  const { symbol, code } = currency;
  if (symbol != null && String(symbol).trim() !== '') return symbol;
  return code || '';
}

/** Keeps persisted checkout currency visible when the store list loads late or omits that code. */
function mergeSavedCurrencyIntoOptions(options, savedCode) {
  if (!options?.length) return options;
  const trimmed = savedCode != null ? String(savedCode).trim() : '';
  if (!trimmed) return options;
  if (options.some((c) => c.code === trimmed)) return options;
  return [...options, { code: trimmed, name: trimmed, symbol: null }];
}

const ProductPriceEditSection = () => {
  const { state, updateProduct, currencyOptions, currenciesLoading } = useContext(AppContext);
  const [priceError, setPriceError] = useState('');
  const priceInputFocusedRef = useRef(false);
  const [displayValue, setDisplayValue] = useState(() =>
    Number(state.product.price ?? 0).toFixed(2)
  );

  useEffect(() => {
    if (!priceInputFocusedRef.current) {
      setDisplayValue(Number(state.product.price ?? 0).toFixed(2));
    }
  }, [state.product.price]);

  const displayCurrencyOptions = useMemo(
    () => mergeSavedCurrencyIntoOptions(currencyOptions, state.product.currency),
    [currencyOptions, state.product.currency]
  );

  const selectedCurrency = useMemo(
    () => displayCurrencyOptions.find((c) => c.code === state.product.currency),
    [displayCurrencyOptions, state.product.currency]
  );

  const amountPrefix = displaySymbolForCurrency(selectedCurrency);

  const handlePriceInputChange = (e) => {
    const raw = e.target.value;
    if (priceError) setPriceError('');
    if (raw !== '' && !/^\d*\.?\d{0,2}$/.test(raw)) return;
    setDisplayValue(raw);
    if (raw === '' || raw === '.') {
      updateProduct({ price: 0 });
      return;
    }
    const n = parseFloat(raw);
    if (!Number.isNaN(n)) {
      updateProduct({ price: n });
    }
  };

  const handlePriceInputFocus = () => {
    priceInputFocusedRef.current = true;
  };

  const handlePriceInputBlur = () => {
    priceInputFocusedRef.current = false;
    const normalized = (parseFloat(displayValue) || 0).toFixed(2);
    setDisplayValue(normalized);
    updateProduct({ price: parseFloat(normalized) });
    setPriceError(validatePrice(normalized));
  };

  const handleCurrencyChange = (value) => {
    updateProduct({ currency: value });
  };

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 0.5, display: 'block', letterSpacing: 0.5 }}
      >
        Price *
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box
          sx={(theme) => ({
            border: `1px solid ${
              priceError ? theme.palette.error.main : theme.palette.divider
            }`,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'stretch',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          })}
        >
          <TextField
            variant="standard"
            type="text"
            value={displayValue}
            onChange={handlePriceInputChange}
            onFocus={handlePriceInputFocus}
            onBlur={handlePriceInputBlur}
            required
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                  {amountPrefix}
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              px: 1.5,
              '& .MuiInput-root': { marginTop: 0, marginBottom: 0 },
              '& input': { py: 1.5 },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              pr: 0.5,
              minWidth: 100,
              pl: 0.5,
            }}
          >
            {currenciesLoading && <CircularProgress size={18} />}
            <Select
              value={state.product.currency}
              onChange={(e) => handleCurrencyChange(e.target.value)}
              renderValue={(value) => value}
              disabled={currenciesLoading}
              variant="standard"
              disableUnderline
              sx={{
                flex: 1,
                minWidth: 88,
                '& .MuiSelect-select': {
                  py: 1.5,
                  pr: 3,
                  pl: 1,
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              {displayCurrencyOptions.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.symbol && String(c.symbol).trim() !== ''
                    ? `${c.code} ${c.symbol}`
                    : c.code}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        {priceError && <FormHelperText error>{priceError}</FormHelperText>}
      </Box>
    </Box>
  );
};

export default ProductPriceEditSection;
